import { GetStaticProps } from "next";
import { useLiveTranslation } from "../lib/LiveContext";
import { cmsPageProps } from "../lib/pageProps";
import React from "react";
import ContactUs from "../components/press/ContactUs";
import InTheNews from "../components/press/InTheNews";
import PressComponent from "../components/press/Press";
import SocialMedia from "../components/press/SocialMedia";
import { Press } from "../interfaces";
import Meta from "../layouts/Meta";
import StaticContainer from "../layouts/StaticContainer";
import { getCmsPress } from "../services/cms/press";

interface Props {
  newsList: Press[];
}

const PressPage = ({ newsList }: Props) => {
  const { t } = useLiveTranslation("press");

  const strings = {
    in_the_news: t("news-h2"),
    in_the_news_description: t("news-p"),
    social_media: t("social-h2"),
    social_media_description: t("social-p"),
    press: t("press-h2"),
    press_description: t("press-p"),
    resources: t("resources-h3"),
    contact_us: t("contact-h3"),
    contact_us_description: t("contact-p"),
  };

  // ItemList JSON-LD over every active media mention. Built from the same
  // server-fetched list the page renders, so the schema can never drift from
  // the UI. Entries without an article link still appear (name + position);
  // JSON.stringify drops the undefined `url` for those.
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: strings.in_the_news,
    numberOfItems: newsList.length,
    itemListElement: newsList.map((news, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: news.articleTitle,
      url: news.articleLink || undefined,
    })),
  };

  return (
    <StaticContainer title={t("title")} category="About">
      <Meta
        title={t("title")}
        description={t("meta-description")}
        keywords={t("meta-keyword")}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(itemListSchema).replace(/</g, "\\u003c"),
        }}
      />
      <InTheNews strings={strings} newsList={newsList} loading={false} />
      <SocialMedia strings={strings} />
      <PressComponent t={t} strings={strings} />
      <ContactUs strings={strings} />
    </StaticContainer>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const newsList: Press[] = await getCmsPress();

  return {
    props: {
      newsList,
      ...(await cmsPageProps(locale, ["press"])),
    },
    // 60s, matching every other CMS-fed page on this site. getCmsPress reads
    // two whole collections per regeneration (pressArticles plus publications
    // to resolve each article's publication by ID), so a one-second window
    // made this the most read-expensive page in the estate and put no ceiling
    // on a crawler. Press articles change a few times a month, and the CMS
    // revalidation webhook covers the publish-then-View flow that actually
    // needed to be instant.
    revalidate: 60,
  };
};

export default PressPage;
